import * as THREE from 'three';
import { BitcoinSun } from './BitcoinSun';
import { DysonSphere } from './DysonSphere';
import { ConstructionFleet } from './ConstructionFleet';
import { SolarFlares } from '../effects/SolarFlares';
import { DynamicObjectsManager } from '../objects/DynamicObjectsManager';
import { MempoolNebula } from '../effects/MempoolNebula';
import { HashrateMapper, hashrateMapper } from '../utils/HashrateMapper';
import { createDysonMaterial } from '../materials/DysonMaterial';
import { useGalaxyStore } from '../../core/state/GalaxyStore';
import { eventBus } from '../../core/state/EventBus';
import { visualizationEventBus } from '../events/VisualizationEventBus';
import type { NetworkStats } from '../../core/models/NetworkStats';
import type { Transaction } from '../../core/models/Transaction';

// Event interface for transaction events from UI layer
export interface TransactionEvent {
  id: string;        // Transaction ID (matches Transaction.id)
  value: number;     // Transaction value in satoshis
  feeRate: number;   // Fee rate in sat/vB
  spawnedAt: number; // Timestamp when event was created
}

export interface VisualElementsConfig {
  sunRadius: number;
  sphereRadius: number;
  enableFleets: boolean;
  enableFlares: boolean;
  enableDynamicObjects: boolean;
  enableNebula: boolean;
  qualityLevel: 'low' | 'medium' | 'high' | 'ultra';
}

export interface MiningPoolData {
  name: string;
  hashrate: number;
  color: THREE.Color;
  share: number; // percentage
}

export class VisualElementsManager extends THREE.Group {
  private config: VisualElementsConfig;
  private hashrateMapper: HashrateMapper;
  private _scene: THREE.Scene | null = null;
  
  // Core visual elements
  public bitcoinSun!: BitcoinSun;
  public dysonSphere!: DysonSphere;
  public solarFlares!: SolarFlares;
  public dynamicObjects!: DynamicObjectsManager;
  public mempoolNebula!: MempoolNebula;
  
  // Mining pool fleets
  private constructionFleets: Map<string, ConstructionFleet> = new Map();
  
  // State tracking
  private lastNetworkStats: NetworkStats | null = null;
  private transactionActivity: number = 0;
  private activityHistory: number[] = [];
  private lastUpdate: number = Date.now();
  
  // Event listeners
  private eventHandlers: Map<string, (...args: any[]) => void> = new Map();
  
  constructor(config?: Partial<VisualElementsConfig>) {
    super();
    
    this.config = {
      sunRadius: 10,
      sphereRadius: 20,
      enableFleets: true,
      enableFlares: true,
      enableDynamicObjects: true,
      enableNebula: true,
      qualityLevel: 'high',
      ...config,
    };
    
    this.hashrateMapper = hashrateMapper;
    
    this.createVisualElements();
    this.setupEventListeners();
    this.setupVisualizationEventListeners();
    this.connectToStore();
    
    this.userData = { id: 'visual-elements-manager', type: 'manager' };
  }
  
  private createVisualElements(): void {
    // Create Bitcoin Sun
    this.bitcoinSun = new BitcoinSun({
      radius: this.config.sunRadius,
    });
    this.add(this.bitcoinSun);
    
    // Create Dyson Sphere
    const dysonMaterial = createDysonMaterial.standard();
    this.dysonSphere = new DysonSphere(
      {
        radius: this.config.sphereRadius,
        segmentCount: this.getSegmentCountForQuality(),
      },
      this.hashrateMapper,
      dysonMaterial
    );
    this.add(this.dysonSphere);
    
    // Create Solar Flares
    if (this.config.enableFlares) {
      this.solarFlares = new SolarFlares(this.config.sunRadius, {
        frequency: 0.1,
        intensity: 1.0,
        duration: 3.0,
      });
      this.add(this.solarFlares);
    }
    
    // Create Mempool Nebula
    if (this.config.enableNebula) {
      this.mempoolNebula = new MempoolNebula({
        center: new THREE.Vector3(0, 0, 0),
        innerRadius: this.config.sunRadius * 1.5,
        outerRadius: this.config.sphereRadius * 1.2,
        height: this.config.sunRadius * 2,
        maxParticles: this.getNebulaParticleCount(),
        qualityLevel: this.config.qualityLevel,
      });
      this.add(this.mempoolNebula);
    }
    
    // Dynamic objects will be initialized after this manager is added to scene
    
    // Set initial quality level
    this.setQualityLevel(this.config.qualityLevel);
  }
  
  private getSegmentCountForQuality(): number {
    const qualityMap = {
      low: 200,
      medium: 500,
      high: 1000,
      ultra: 2000,
    };
    return qualityMap[this.config.qualityLevel];
  }
  
  private getNebulaParticleCount(): number {
    const qualityMap = {
      low: 300,
      medium: 600,
      high: 1000,
      ultra: 1500,
    };
    return qualityMap[this.config.qualityLevel];
  }
  
  private setupEventListeners(): void {
    // Network stats updates
    const statsHandler = (event: any) => {
      this.handleNetworkStatsUpdate(event.data);
    };
    eventBus.on('stats_update', statsHandler);
    this.eventHandlers.set('stats_update', statsHandler);
    
    // Transaction events
    const transactionHandler = (event: any) => {
      this.handleTransactionEvent(event.data);
    };
    eventBus.on('transaction', transactionHandler);
    this.eventHandlers.set('transaction', transactionHandler);
    
    // Block events (for major network activity)
    const blockHandler = (event: any) => {
      this.handleBlockEvent(event.data);
    };
    eventBus.on('block', blockHandler);
    this.eventHandlers.set('block', blockHandler);
  }

  private setupVisualizationEventListeners(): void {
    // Listen for transaction events from UI layer
    visualizationEventBus.on('transaction', (data) => {
      this.handleTransactionEvent(data as TransactionEvent);
    });

    // Listen for network stats updates
    visualizationEventBus.on('network_stats', (data: { hashrate: number; difficulty: number }) => {
      this.handleNetworkStatsUpdate({
        hashrate: data.hashrate,
        difficulty: data.difficulty,
        mempool: { usage: 0, size: 0, txCount: 0, totalFee: 0 },
        nextAdjustment: { blocksRemaining: 0, estimatedTime: 0, estimatedChange: 0 },
        mempoolSize: 0,
        mempoolTxCount: 0,
        feeEstimates: { fastestFee: 0, halfHourFee: 0, hourFee: 0, economyFee: 0, minimumFee: 0 },
        currentHeight: 0
      });
    });

    // Listen for system control events
    visualizationEventBus.on('system_pause', () => {
      this.pauseAnimations();
    });

    visualizationEventBus.on('system_resume', () => {
      this.resumeAnimations();
    });
  }
  
  private connectToStore(): void {
    // Get initial state from store
    const store = useGalaxyStore.getState();
    
    // Apply initial network stats if available
    if (store.network.hashrate > 0) {
      this.handleNetworkStatsUpdate({
        hashrate: store.network.hashrate,
        difficulty: store.network.difficulty,
        nextAdjustment: store.network.nextAdjustment,
      } as NetworkStats);
    }
    
    // Calculate initial transaction activity
    const transactions = Array.from(store.blockchain.transactions.values());
    if (transactions.length > 0) {
      const recentTxs = transactions.filter(
        tx => Date.now() - tx.spawnedAt < 60000 // Last minute
      );
      this.transactionActivity = recentTxs.length / 60; // tx/second
    }
    
  }
  
  private handleNetworkStatsUpdate(stats: NetworkStats): void {
    this.lastNetworkStats = stats;
    
    // Update hashrate mapper and Dyson sphere
    this.dysonSphere.updateHashrate(stats.hashrate);
    
    // Update sun health status based on mempool congestion
    const mempoolUsage = stats.mempool?.usage || 0;
    let healthStatus: 'healthy' | 'congested' | 'critical' = 'healthy';
    
    if (mempoolUsage > 80) {
      healthStatus = 'critical';
    } else if (mempoolUsage > 50) {
      healthStatus = 'congested';
    }
    
    this.bitcoinSun.setHealthStatus(healthStatus);
    
    // Update mining pool fleets if we have pool data
    this.updateMiningPoolFleets(stats);
    
    // Update mempool nebula if enabled
    if (this.config.enableNebula && this.mempoolNebula && stats.mempool) {
      const mempoolStats = {
        size: stats.mempoolSize || stats.mempool.size || 0,
        count: stats.mempoolTxCount || stats.mempool.txCount || 0,
        feeHistogram: [
          { feeRate: stats.feeEstimates?.economyFee || 1, count: 40 },
          { feeRate: stats.feeEstimates?.hourFee || 10, count: 30 },
          { feeRate: stats.feeEstimates?.halfHourFee || 50, count: 20 },
          { feeRate: stats.feeEstimates?.fastestFee || 100, count: 10 },
        ],
        avgFeeRate: (stats.feeEstimates?.hourFee || 10),
      };
      this.mempoolNebula.updateMempoolStats(mempoolStats);
    }
    
    // Trigger activity-based effects
    const hashrateChange = this.lastNetworkStats ? 
      stats.hashrate - this.lastNetworkStats.hashrate : 0;
    
    if (Math.abs(hashrateChange) > 10 && this.solarFlares) {
      // Significant hashrate change triggers flares
      this.solarFlares.triggerActivityFlare(Math.abs(hashrateChange));
    }
  }
  
  public handleTransactionEvent(transactionEvent: TransactionEvent): void {
    // Update transaction activity tracking
    this.activityHistory.push(Date.now());
    
    // Keep only last minute of activity
    const oneMinuteAgo = Date.now() - 60000;
    this.activityHistory = this.activityHistory.filter(time => time > oneMinuteAgo);
    
    // Calculate current activity rate
    this.transactionActivity = this.activityHistory.length / 60; // tx/second
    
    // Update sun activity
    this.bitcoinSun.updateActivity(this.transactionActivity);
    
    // Update solar flares
    if (this.solarFlares) {
      this.solarFlares.updateActivity(this.transactionActivity);
    }
    
    // Spawn transaction in mempool nebula
    if (this.config.enableNebula && this.mempoolNebula) {
      const transaction: Transaction = {
        id: transactionEvent.id,
        amount: transactionEvent.value,
        fee: Math.floor(transactionEvent.feeRate * 250), // Estimate fee
        feeRate: transactionEvent.feeRate,
        size: 250, // Default size
        timestamp: transactionEvent.spawnedAt,
        inputs: [],
        outputs: [],
        status: 'pending',
      };
      this.mempoolNebula.spawnTransaction(transaction);
    }
    
    // High-value transactions trigger special effects
    if (transactionEvent.value > 10000000) { // > 0.1 BTC
      this.bitcoinSun.pulse(0.3, 1000); // 30% pulse for 1 second
      
      if (this.solarFlares) {
        this.solarFlares.triggerFlare();
      }
    }
  }
  
  private handleBlockEvent(_blockData: any): void {
    // Major network event - trigger significant visual response
    this.bitcoinSun.pulse(0.5, 2000); // 50% pulse for 2 seconds
    
    if (this.solarFlares) {
      // Multiple flares for block events
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          this.solarFlares.triggerFlare(undefined, 1.5);
        }, i * 500);
      }
    }
  }
  
  private updateMiningPoolFleets(stats: NetworkStats): void {
    if (!this.config.enableFleets) return;
    
    // This would typically come from mining pool API data
    // For now, simulate some major pools
    const simulatedPools: MiningPoolData[] = [
      {
        name: 'AntPool',
        hashrate: stats.hashrate * 0.18,
        color: new THREE.Color(0xff6600),
        share: 18,
      },
      {
        name: 'Foundry USA',
        hashrate: stats.hashrate * 0.16,
        color: new THREE.Color(0x0066ff),
        share: 16,
      },
      {
        name: 'F2Pool',
        hashrate: stats.hashrate * 0.14,
        color: new THREE.Color(0x66ff00),
        share: 14,
      },
      {
        name: 'Binance Pool',
        hashrate: stats.hashrate * 0.12,
        color: new THREE.Color(0xffff00),
        share: 12,
      },
      {
        name: 'ViaBTC',
        hashrate: stats.hashrate * 0.10,
        color: new THREE.Color(0xff0066),
        share: 10,
      },
    ];
    
    // Update existing fleets or create new ones
    for (const poolData of simulatedPools) {
      let fleet = this.constructionFleets.get(poolData.name);
      
      if (!fleet) {
        // Create new fleet
        fleet = new ConstructionFleet(
          poolData.name,
          poolData.hashrate,
          {
            color: poolData.color,
            maxDrones: Math.min(Math.floor(poolData.share), 20),
          }
        );
        
        this.constructionFleets.set(poolData.name, fleet);
        this.add(fleet);
        
        // Add fleet to Dyson sphere
        this.dysonSphere.addConstructionFleet(
          poolData.name,
          poolData.hashrate,
          poolData.color
        );
      } else {
        // Update existing fleet
        fleet.poolHashrate = poolData.hashrate;
      }
      
      // Position fleet around sphere
      const angle = (simulatedPools.indexOf(poolData) / simulatedPools.length) * Math.PI * 2;
      const radius = this.config.sphereRadius * 1.5;
      const fleetPosition = new THREE.Vector3(
        Math.cos(angle) * radius,
        0,
        Math.sin(angle) * radius
      );
      
      fleet.updatePosition(fleetPosition);
    }
  }
  
  // Initialize dynamic objects after being added to scene
  private initializeDynamicObjectsIfNeeded(): void {
    if (this.config.enableDynamicObjects && !this.dynamicObjects && this._scene) {
      console.log('Initializing DynamicObjectsManager...');
      this.dynamicObjects = new DynamicObjectsManager(this._scene, {
        qualityLevel: this.config.qualityLevel,
        enableTrails: this.config.qualityLevel !== 'low',
        enableDocking: true,
      });
      this.add(this.dynamicObjects);
      console.log('DynamicObjectsManager initialized and added to scene');
    } else if (this.config.enableDynamicObjects && !this.dynamicObjects) {
      // Find the scene if not already found
      let current: THREE.Object3D | null = this;
      while (current && !(current instanceof THREE.Scene)) {
        current = current.parent;
      }
      
      if (current instanceof THREE.Scene) {
        this._scene = current;
        console.log('Initializing DynamicObjectsManager...');
        this.dynamicObjects = new DynamicObjectsManager(this._scene, {
          qualityLevel: this.config.qualityLevel,
          enableTrails: this.config.qualityLevel !== 'low',
          enableDocking: true,
        });
        this.add(this.dynamicObjects);
        console.log('DynamicObjectsManager initialized and added to scene');
      }
    }
  }

  public getBitcoinSun(): BitcoinSun {
    return this.bitcoinSun;
  }
  
  public getDysonSphere(): DysonSphere {
    return this.dysonSphere;
  }
  
  public getSolarFlares(): SolarFlares | undefined {
    return this.solarFlares;
  }
  
  public getMempoolNebula(): MempoolNebula | undefined {
    return this.mempoolNebula;
  }
  
  public getConstructionFleets(): Map<string, ConstructionFleet> {
    return this.constructionFleets;
  }
  
  public update(deltaTime: number): void {
    // Initialize dynamic objects if not already done
    this.initializeDynamicObjectsIfNeeded();
    
    // Update all visual elements
    this.bitcoinSun.update(deltaTime);
    this.dysonSphere.update(deltaTime);
    
    if (this.solarFlares) {
      this.solarFlares.update(deltaTime);
    }
    
    if (this.dynamicObjects) {
      this.dynamicObjects.update(deltaTime);
    }
    
    if (this.mempoolNebula) {
      this.mempoolNebula.update(deltaTime);
    }
    
    // Update construction fleets
    for (const fleet of this.constructionFleets.values()) {
      fleet.update(deltaTime);
    }
    
    // Update activity decay
    this.updateActivityDecay();
  }
  
  private updateActivityDecay(): void {
    // Gradually reduce activity if no recent transactions
    const now = Date.now();
    const timeSinceLastUpdate = now - this.lastUpdate;
    
    if (timeSinceLastUpdate > 5000) { // 5 seconds
      this.transactionActivity *= 0.95; // 5% decay
      this.bitcoinSun.updateActivity(this.transactionActivity);
      
      if (this.solarFlares) {
        this.solarFlares.updateActivity(this.transactionActivity);
      }
      
      this.lastUpdate = now;
    }
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    this.config.qualityLevel = level;
    
    // Update quality for all components
    this.dysonSphere.setQualityLevel(level);
    
    if (this.solarFlares) {
      this.solarFlares.setQualityLevel(level);
    }
    
    if (this.dynamicObjects) {
      this.dynamicObjects.setQualityLevel(level);
    }
    
    if (this.mempoolNebula) {
      this.mempoolNebula.setQualityLevel(level);
    }
    
    // Adjust fleet animation quality
    const animationSettings = {
      low: { particleEffects: false, laserBeams: false },
      medium: { particleEffects: false, laserBeams: true },
      high: { particleEffects: true, laserBeams: true },
      ultra: { particleEffects: true, laserBeams: true },
    };
    
    const settings = animationSettings[level];
    for (const fleet of this.constructionFleets.values()) {
      fleet.setAnimationSettings(settings);
    }
  }
  
  public getSystemStatus(): {
    sunActivity: number;
    sphereCompletion: number;
    activeFlares: number;
    activeFleets: number;
    activeShips: number;
    activePlanets: number;
    performanceImpact: 'low' | 'medium' | 'high';
  } {
    const sphereInfo = this.dysonSphere.getConstructionInfo();
    const flareCount = this.solarFlares?.getActiveFlareCount() || 0;
    const dynamicStatus = this.dynamicObjects?.getStatus() || { activeShips: 0, planetCount: 0 };
    
    // Estimate performance impact
    let performanceImpact: 'low' | 'medium' | 'high' = 'low';
    const totalObjects = sphereInfo.segmentsCompleted + sphereInfo.segmentsBuilding + 
                        flareCount + this.constructionFleets.size + 
                        dynamicStatus.activeShips + dynamicStatus.planetCount;
    
    if (totalObjects > 2000) {
      performanceImpact = 'high';
    } else if (totalObjects > 1000) {
      performanceImpact = 'medium';
    }
    
    return {
      sunActivity: this.transactionActivity,
      sphereCompletion: sphereInfo.completionPercentage,
      activeFlares: flareCount,
      activeFleets: this.constructionFleets.size,
      activeShips: dynamicStatus.activeShips,
      activePlanets: dynamicStatus.planetCount,
      performanceImpact,
    };
  }
  
  public triggerCustomFlare(direction?: THREE.Vector3, intensity?: number): string | undefined {
    return this.solarFlares?.triggerFlare(direction, intensity);
  }
  
  public pauseAnimations(): void {
    // Pause all animations for performance
    for (const fleet of this.constructionFleets.values()) {
      fleet.setAnimationSettings({
        particleEffects: false,
        laserBeams: false,
        progressIndicators: false,
        sparkIntensity: 0,
      });
    }
    
    if (this.solarFlares) {
      this.solarFlares.setFrequency(0);
    }
  }
  
  public resumeAnimations(): void {
    // Resume animations
    const level = this.config.qualityLevel;
    this.setQualityLevel(level);
    
    if (this.solarFlares) {
      this.solarFlares.setFrequency(0.1);
    }
  }
  
  public dispose(): void {
    // Remove event listeners
    for (const [eventName, handler] of this.eventHandlers.entries()) {
      eventBus.off(eventName, handler);
    }
    this.eventHandlers.clear();
    
    // Dispose visual elements
    this.bitcoinSun.dispose();
    this.dysonSphere.dispose();
    
    if (this.solarFlares) {
      this.solarFlares.dispose();
    }
    
    if (this.dynamicObjects) {
      this.dynamicObjects.dispose();
    }
    
    if (this.mempoolNebula) {
      this.mempoolNebula.dispose();
    }
    
    // Dispose construction fleets
    for (const fleet of this.constructionFleets.values()) {
      fleet.dispose();
    }
    this.constructionFleets.clear();
    
    // Clear state
    this.activityHistory = [];
  }
}